import { z } from 'zod';
import { localDateSchema } from './contracts/common';

/**
 * The `FrequencyRule` v1 JSON document (PLAN §3.3 sec. 6.3, D-057) as a Zod schema, for request
 * bodies (`frequencyRule` of the recurring-duty endpoints, import value maps) and API responses.
 * `@orbit/domain/ptstdm` validates the stored document with `safeParseFrequencyRule`, which has no
 * Zod dependency (D-161); a test in `packages/domain/src/ptstdm/frequencySchema.test.ts` keeps the
 * two in agreement. Strict: unknown keys are rejected everywhere.
 */

export const FREQUENCY_WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export const FREQUENCY_NON_WORKING_DAY_RULES = [
  'next_working_day',
  'previous_working_day',
  'skip',
  'keep',
] as const;

const weekday = z.enum(FREQUENCY_WEEKDAYS);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected a time HH:mm');
/** `trigger_event_types.key`, for example `customer.onboarded`. */
const EVENT_KEY = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)*$/;
/** A payload field holding a user id, for example `csmUserId`. */
const PAYLOAD_FIELD = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** `{ day }` (1..31 or -1), `{ nth, weekday }` (nth 1..5 or -1) or `{ workingDay }` (±1..31). */
export const dayAnchorSchema = z.union([
  z.strictObject({
    day: z.int().refine((d) => d === -1 || (d >= 1 && d <= 31), 'Expected 1..31 or -1'),
  }),
  z.strictObject({
    nth: z.union([
      z.literal(1),
      z.literal(2),
      z.literal(3),
      z.literal(4),
      z.literal(5),
      z.literal(-1),
    ]),
    weekday,
  }),
  z.strictObject({
    workingDay: z
      .int()
      .min(-31)
      .max(31)
      .refine((n) => n !== 0, 'Expected a non-zero integer from -31 to 31'),
  }),
]);

const base = {
  v: z.literal(1),
  startDate: localDateSchema.optional(),
  endDate: localDateSchema.optional(),
};
const calendarBase = {
  ...base,
  nonWorkingDay: z.enum(FREQUENCY_NON_WORKING_DAY_RULES).optional(),
};

const byKind = z.discriminatedUnion('kind', [
  z.strictObject({ ...calendarBase, kind: z.literal('daily') }),
  z.strictObject({
    ...calendarBase,
    kind: z.literal('weekdays'),
    days: z
      .array(weekday)
      .min(1)
      .refine((days) => new Set(days).size === days.length, 'Weekdays must be distinct'),
  }),
  z
    .strictObject({
      ...calendarBase,
      kind: z.literal('weekly'),
      day: weekday,
      everyNWeeks: z.int().min(1).max(52).optional(),
      anchorDate: localDateSchema.optional(),
    })
    .refine((r) => !(r.everyNWeeks !== undefined && r.everyNWeeks > 1 && !r.anchorDate), {
      path: ['anchorDate'],
      message: 'Required when everyNWeeks > 1',
    }),
  z.strictObject({
    ...calendarBase,
    kind: z.literal('monthly'),
    on: dayAnchorSchema,
    everyNMonths: z.int().min(1).max(12).optional(),
  }),
  z.strictObject({
    ...calendarBase,
    kind: z.literal('quarterly'),
    monthOfQuarter: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    on: dayAnchorSchema,
  }),
  z.strictObject({
    ...calendarBase,
    kind: z.literal('yearly'),
    month: z.int().min(1).max(12),
    on: dayAnchorSchema,
  }),
  z.strictObject({
    ...base,
    kind: z.literal('on_trigger'),
    event: z.string().regex(EVENT_KEY, 'Expected a trigger event key such as customer.onboarded'),
    due: z.union([
      z.strictObject({ hoursAfter: z.number().gt(0).max(8760) }),
      z.strictObject({ workingDaysAfter: z.int().min(0).max(250), time: hhmm.optional() }),
    ]),
    assignee: z.union([
      z.literal('position_holders'),
      z.strictObject({ fromPayload: z.string().regex(PAYLOAD_FIELD) }),
    ]),
  }),
]);

export const frequencyRuleSchema = byKind.refine(
  (r) => !(r.startDate && r.endDate && r.endDate < r.startDate),
  { path: ['endDate'], message: 'endDate is before startDate' },
);

export type FrequencyRuleDocument = z.infer<typeof frequencyRuleSchema>;
